import type { ComponentContentSchema, SlotGroupDefinition } from '@vibesites/runtime';
import {
  usePropertyPhone,
  useSlotGroup,
  EditableSlotGroup,
  RichTextContent,
} from '@vibesites/runtime/react';
import { VibesitesImage } from '@vibesites/ui-primitives';
import { clean, formatPhone, num, stagger, stripHtml, telHref, galleryPhotos } from '../utils/format';
import { useIsEditing, useRawProperty } from '../utils/hooks';
import { byDist, miles, type Poi } from '../utils/places';
import { PATHS } from '../utils/site';
import { petCells } from '../utils/fees';

interface Photo {
  image?: string;
  alt_text?: string;
  title?: string;
}

const ctaDef: SlotGroupDefinition = {
  label: 'Pet Policy Button',
  fields: {
    label: { label: 'Button Text', type: 'text', defaultValue: 'Read the Pet Policy' },
    url: { label: 'URL', type: 'url', defaultValue: '/pet-policy' },
    _target: { label: 'Target', type: 'text', defaultValue: '_self' },
  },
};

const photoDef: SlotGroupDefinition = {
  label: 'Photo',
  description: 'Until set, a gallery photo of an on-site dog park is shown, else a community photo.',
  fields: {
    image: { label: 'Image', type: 'image', defaultField: 'photo_gallery[0].image' },
    alt: { label: 'Alt Text', type: 'text', defaultField: 'photo_gallery[0].alt_text' },
    caption: { label: 'Caption', type: 'text', defaultValue: 'Around the community' },
  },
  sourceBinding: { collection: 'photo_gallery', defaultIndex: 0, fieldMappings: { image: 'image', alt: 'alt_text', caption: 'title' } },
  generator: {
    prompt: 'A gallery photo that suits pet owners: an on-site dog park or pet amenity if one is pictured, otherwise open lawn, trails or green space at the community. Leave empty if nothing fits. Caption it in two to four words naming what is shown.',
    context: 'Photo beside the pet policy facts on the Amenities page. Caption is a short pill over the photo. Do not reuse the amenity highlight photos above it if another fits.',
  },
};

const DOG = /dog park|dog run|paw park/i;

/** Nearest off-leash park within 5 miles, else the nearest park (within `parkCap` miles when given). */
function parkNote(pois: Poi[], parkCap?: number): string {
  const dogParks = pois.filter((x) => x.poi_place_primary_type === 'dog_park' || DOG.test(x.poi_name || '')).sort(byDist);
  const parks = pois.filter((x) => /park/i.test(x.poi_place_primary_type || '') && (parkCap == null || (miles(x) ?? 99) <= parkCap)).sort(byDist);
  const dist = (x: Poi) => String(x.poi_miles_from_property).replace(' mi', ' miles');
  if (dogParks[0] && (miles(dogParks[0]) ?? 99) <= 5) return `Nearest off-leash park: ${dogParks[0].poi_name}, ${dist(dogParks[0])}.`;
  if (parks[0]) return `Nearest park: ${parks[0].poi_name}, ${dist(parks[0])}.`;
  return '';
}

interface PetPolicyProps {
  /** "full": the Pet Policy page's at-a-glance band (description, four facts in a row, park and phone footnote); no photo or button. */
  variant?: 'summary' | 'full';
}

export function PetPolicy({ variant = 'summary' }: PetPolicyProps) {
  const p = useRawProperty();
  const editing = useIsEditing();
  const phone = formatPhone(usePropertyPhone());
  const cta = useSlotGroup('petpolicy.cta', ctaDef);
  const photoSlot = useSlotGroup('petpolicy.photo', photoDef);

  if (!p.property_ispetfriendly && !editing) return null;


  const cells = petCells(p);
  const pois = (p.neighborhood_pois as Poi[]) || [];

  if (variant === 'full') {
    const anyFact =
      !!p.property_allows_dogs || !!p.property_allows_cats || [p.property_max_pets, p.property_upfront_pet_fee, p.property_monthly_pet_rent].some((v) => num(clean(v)));
    const park = parkNote(pois, 10);
    const tel = telHref(phone);
    const hasDescription = !!stripHtml(clean(p.property_pet_policy_description) || '');
    return (
      <section className="section section--surface flex flex-col gap-10 pt-24 max-[1199px]:gap-8 max-[767px]:gap-6" id="pets">
        <div className="flex max-w-[760px] flex-col gap-4 max-[767px]:gap-3" data-reveal>
          <RichTextContent path="petpolicy.glance_eyebrow" as="div" className="eyebrow" />
          <RichTextContent path="petpolicy.glance_title" as="h2" className="h2" />
          {(hasDescription || editing) && (
            <RichTextContent
              path="petpolicy.glance_lead"
              as="div"
              className="m-0 text-lg leading-7 text-muted-foreground [&_p]:m-0 max-[1199px]:text-[17px] max-[1199px]:leading-[27px] max-[767px]:text-[15px] max-[767px]:leading-[22px]"
              defaultField="property_pet_policy_description"
            />
          )}
        </div>
        {anyFact && (
          <div className="grid grid-cols-4 gap-3 max-[1199px]:grid-cols-2 max-[767px]:gap-2.5" data-reveal style={stagger(1)}>
            {cells.map(([l, v, s]) => (
              <div className="flex flex-col gap-2 rounded-lg border border-[--q-border] bg-[--q-paper] px-6 py-5 max-[767px]:gap-1.5 max-[767px]:px-[18px] max-[767px]:py-4" key={l}>
                <div className="label">{l}</div>
                <div className="flex flex-wrap items-baseline gap-2 font-heading text-[26px] leading-[30px] max-[767px]:gap-1.5 max-[767px]:text-[22px] max-[767px]:leading-[26px]">
                  {v}
                  {s && <small className="font-body text-[14px] leading-5 text-muted-foreground max-[767px]:text-[13px] max-[767px]:leading-[18px]">{s}</small>}
                </div>
              </div>
            ))}
          </div>
        )}
        {(park || tel) && (
          <p className="m-0 text-sm leading-5 text-muted-foreground [&_a]:font-medium [&_a]:text-[--q-primary-on-paper] [&_a]:underline-offset-[3px] max-[767px]:text-[13px] max-[767px]:leading-[19px]">
            {park}
            {tel && (
              <>
                {park && ' '}Questions about a specific pet? Call the leasing team at{' '}
                <a className="dniphone dniphonehref" href={tel}>
                  {phone}
                </a>
                .
              </>
            )}
          </p>
        )}
      </section>
    );
  }

  let note = parkNote(pois);
  const restriction = stripHtml(clean(p.property_pet_restrictions) || '').split(/(?<=[.!?])\s/)[0];
  if (restriction && !/contact management/i.test(restriction)) note += (note ? ' ' : '') + restriction;

  const photos = galleryPhotos<Photo>(p.photo_gallery);
  const chosen = photoSlot.image && photoSlot.image !== photos[0]?.image;
  const auto = photos.find((g) => DOG.test(g.alt_text || '')) || photos.find((g) => g.title === 'Community');
  const photo = chosen
    ? { src: photoSlot.image!, alt: photoSlot.alt || '', caption: photoSlot.caption || '' }
    : auto
      ? { src: auto.image, alt: auto.alt_text || '', caption: DOG.test(auto.alt_text || '') ? 'On-site dog park' : 'Around the community' }
      : null;

  return (
    <section className="section section--surface flex items-stretch gap-16 max-[1199px]:flex-col max-[767px]:gap-4" id="pets">
      <EditableSlotGroup
        groupPath="petpolicy.photo"
        definition={photoDef}
        className={
          photo
            ? 'photo min-h-[520px] w-[592px] shrink-0 max-[1199px]:h-[360px] max-[1199px]:min-h-[360px] max-[1199px]:w-auto max-[767px]:h-[240px] max-[767px]:min-h-[240px]'
            : ''
        }
      >
        {photo && (
          <>
            <VibesitesImage url={photo.src} alt={photo.alt} sizes="(min-width: 1200px) 592px, 100vw" />
            {photo.caption && <div className="pill pill--paper photo__caption">{photo.caption}</div>}
          </>
        )}
      </EditableSlotGroup>
      <div className="flex grow flex-col gap-6 max-[767px]:gap-4" data-reveal style={stagger(1)}>
        <RichTextContent path="petpolicy.eyebrow" as="div" className="eyebrow" />
        <RichTextContent path="petpolicy.title" as="h2" className="h2" />
        <RichTextContent
          path="petpolicy.lead"
          as="p"
          className="m-0 leading-[26px] text-muted-foreground max-[767px]:text-[15px] max-[767px]:leading-[23px]"
        />
        <div className="grid grid-cols-[1fr_1fr] gap-3 max-[767px]:gap-2.5">
          {cells.map(([l, v, s]) => (
            <div
              className="flex flex-col gap-2 rounded-lg border border-[--q-border] bg-[--q-paper] px-6 py-5 max-[767px]:gap-1.5 max-[767px]:p-4"
              key={l}
            >
              <div className="label">{l}</div>
              <div className="flex flex-wrap items-baseline gap-2 font-heading text-[26px] leading-[30px] max-[767px]:gap-1.5 max-[767px]:text-[22px] max-[767px]:leading-[26px]">
                {v}
                {s && <small className="font-body text-[14px] leading-5 text-muted-foreground max-[767px]:text-[13px] max-[767px]:leading-[18px]">{s}</small>}
              </div>
            </div>
          ))}
        </div>
        {note && <div className="text-sm leading-5 text-muted-foreground max-[767px]:text-[13px] max-[767px]:leading-[19px]">{note}</div>}
        <div className="flex gap-3 pt-1 max-[767px]:flex-col">
          <EditableSlotGroup groupPath="petpolicy.cta" definition={ctaDef}>
            <a className="btn btn-secondary" href={cta.url || PATHS.petPolicy} target={cta._target || '_self'}>
              {cta.label || 'Read the Pet Policy'} <span className="btn__arrow">→</span>
            </a>
          </EditableSlotGroup>
        </div>
      </div>
    </section>
  );
}

export const contentSchema: ComponentContentSchema = {
  slot: 'PetPolicy',
  displayName: 'Pet Policy',
  tokens: {
    'petpolicy.eyebrow': { label: 'Eyebrow', type: 'richtext', defaultValue: '<p>Pets</p>' },
    'petpolicy.title': {
      label: 'Title',
      type: 'richtext',
      defaultValue: '<p>Pets are welcome here</p>',
      generator: {
        prompt: 'A short, warm headline welcoming pets at this property. No closing period.',
        context: 'H2 of the Pets section on the Amenities page, above the lead and the pet facts (kinds allowed, limits, fees, pet rent).',
        length: '3–7 words',
      },
    },
    'petpolicy.lead': {
      label: 'Lead',
      description: 'Mapped to the Pets Blurb field. Type over it to replace it, or pick another field from the suggestions.',
      type: 'richtext',
      defaultField: 'property_pets_blurb',
      defaultValue: '<p>Your pets are part of the household at <span data-type="field-token" data-field="property_name"></span>.</p>',
      suggestedFields: ['property_pets_blurb', 'property_pet_policy_description'],
      generator: {
        prompt: 'A lead on life with pets here: the pet-friendly features and nearby parks this property really has. Do not state fees, limits or breed rules; the facts beside it cover those.',
        context: 'Lead under the Pets headline on the Amenities page, above the pet facts and the Read the Pet Policy button.',
        length: '1–2 sentences',
      },
    },
    'petpolicy.glance_eyebrow': {
      label: 'At a Glance · Eyebrow',
      description: 'Pet Policy page only.',
      type: 'richtext',
      defaultValue: '<p>At a glance</p>',
    },
    'petpolicy.glance_title': {
      label: 'At a Glance · Title',
      description: 'Pet Policy page only.',
      type: 'richtext',
      defaultValue: '<p>The policy in one breath.</p>',
    },
    'petpolicy.glance_lead': {
      label: 'At a Glance · Lead',
      description: 'Pet Policy page only. The pet policy description as written; hidden when it is empty.',
      type: 'richtext',
      defaultField: 'property_pet_policy_description',
    },
  },
  slotGroups: {
    'petpolicy.photo': photoDef,
    'petpolicy.cta': ctaDef,
  },
};

(PetPolicy as unknown as { contentSchema: ComponentContentSchema }).contentSchema = contentSchema;

export default PetPolicy;
